import { useEffect, useRef, useState } from "react";
import { downloadFile, queryString } from "lib/siteCustomerApi";
import { Notice, Remote, Pager, bytes, date, useRemote } from "./Shared";
import PdfPreview from "./PdfPreview";
export function Download({ resource }) {
  const [state, setState] = useState({}),
    abort = useRef(null);
  useEffect(() => () => abort.current?.abort(), []);
  return (
    <>
      <button
        className="ca-primary"
        disabled={state.busy}
        onClick={async () => {
          if (abort.current) return;
          const controller = new AbortController();
          abort.current = controller;
          setState({ busy: true, received: 0 });
          try {
            await downloadFile(resource, {
              signal: controller.signal,
              onProgress: (received) => setState({ busy: true, received }),
            });
            setState({ done: true });
          } catch (error) {
            if (!controller.signal.aborted)
              setState(error.name === "AbortError" ? {} : { error });
          } finally {
            abort.current = null;
          }
        }}
      >
        {state.busy
          ? "Downloading…"
          : state.error
            ? "Start a fresh download"
            : "Download"}
      </button>
      {state.busy && (
        <>
          <progress
            max={resource.file.size}
            value={state.received}
            aria-label={`Downloading ${resource.title}`}
          />
          <button
            onClick={() => {
              abort.current?.abort();
              setState({ cancelled: true });
            }}
          >
            Cancel download
          </button>
        </>
      )}
      {state.done && (
        <p role="status">
          File prepared. Check your browser downloads or chosen location.
        </p>
      )}
      {state.cancelled && (
        <p role="status">Download cancelled. You can start a fresh download.</p>
      )}
      <Notice error={state.error} />
    </>
  );
}
export default function Library() {
  const [draft, setDraft] = useState({
      prefix: "",
      category: "",
      availability: "all",
    }),
    [filters, setFilters] = useState(draft),
    [pages, setPages] = useState([null]),
    [tick, setTick] = useState(0),
    [preview, setPreview] = useState(null);
  const cursor = pages.at(-1),
    query = queryString({
      ...filters,
      ...(typeof cursor === "string" ? { after: cursor } : cursor || {}),
      limit: "24",
    });
  const state = useRemote("/customer/library?" + query, tick);
  return (
    <>
      <div className="ca-section-head">
        <div>
          <p className="ca-eyebrow">Guides and checklists</p>
          <h1>Free resources</h1>
          <p>Free resources and files shared with your account.</p>
        </div>
        <button
          onClick={() => {
            setPreview(null);
            setTick((v) => v + 1);
          }}
        >
          Refresh library
        </button>
      </div>
      <form
        className="ca-filters"
        onSubmit={(e) => {
          e.preventDefault();
          setPreview(null);
          setFilters({ ...draft });
          setPages([null]);
        }}
      >
        <label>
          Title starts with
          <input
            type="search"
            minLength={2}
            maxLength={100}
            placeholder="Search by title"
            value={draft.prefix}
            onChange={(e) => setDraft({ ...draft, prefix: e.target.value })}
          />
        </label>
        <label>
          Category
          <input
            maxLength={100}
            placeholder="All categories"
            list="file-categories"
            value={draft.category}
            onChange={(e) => setDraft({ ...draft, category: e.target.value })}
          />
          <datalist id="file-categories">
            {[
              ...new Set(
                state.data?.items.map((x) => x.category).filter(Boolean) || [],
              ),
            ].map((c) => (
              <option key={c}>{c}</option>
            ))}
          </datalist>
        </label>
        <label>
          Availability
          <select
            value={draft.availability}
            onChange={(e) =>
              setDraft({ ...draft, availability: e.target.value })
            }
          >
            <option value="all">All my files</option>
            <option value="available">Available</option>
            <option value="unavailable">Expired or unavailable</option>
          </select>
        </label>
        <button className="ca-primary">Apply filters</button>
      </form>
      {preview && (
        <PdfPreview
          key={preview.id}
          resource={preview}
          onClose={() => {
            const id = preview.id;
            setPreview(null);
            setTimeout(
              () => document.getElementById("preview-" + id)?.focus(),
              0,
            );
          }}
        />
      )}
      <Remote state={state}>
        {state.data && (
          <>
            {!state.data.items.length && (
              <div className="ca-panel">
                <h2>No files on this page</h2>
                <p>
                  {state.data.next
                    ? "Continue to the next page to check more matching files."
                    : "Try different filters. Files shared with you will appear here."}
                </p>
              </div>
            )}
            <div className="ca-library">
              {state.data.items.map((resource) => (
                <article className="ca-file" key={resource.id}>
                  <div className="ca-file-top">
                    <span>
                      {resource.file?.mime === "application/zip"
                        ? "ZIP"
                        : "PDF / FILE"}
                    </span>
                    <span className="ca-badge">
                      {resource.availability === "available"
                        ? "Available"
                        : resource.availability}
                    </span>
                  </div>
                  <p className="ca-eyebrow">
                    {resource.category || "Resource"}
                  </p>
                  <h2>{resource.title}</h2>
                  <p>{resource.description}</p>
                  {resource.file && (
                    <p className="ca-muted">
                      {bytes(resource.file.size)} · {resource.file.name}
                      <br />
                      Version {resource.file.version.slice(0, 8)}
                    </p>
                  )}
                  {resource.expiresAt && (
                    <p>Access until {date(resource.expiresAt)}</p>
                  )}
                  {resource.availability === "available" ? (
                    <div className="ca-actions">
                      <Download resource={resource} />
                      {resource.hasPreview && (
                        <button
                          id={"preview-" + resource.id}
                          onClick={() => setPreview(resource)}
                        >
                          Preview PDF
                        </button>
                      )}
                    </div>
                  ) : (
                    <p className="ca-notice">
                      {resource.availability === "revoked"
                        ? "Your access was revoked."
                        : resource.availability === "expired"
                          ? "Your access has expired."
                          : "This file is currently unavailable."}{" "}
                      Contact support if you need help.
                    </p>
                  )}
                </article>
              ))}
            </div>
            <Pager next={state.data.next} pages={pages} setPages={setPages} />
          </>
        )}
      </Remote>
    </>
  );
}
